import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  FormHelperText,
  Button,
  Grid,
  TextField,
} from "@mui/material";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useGetDDLAccountListQuery} from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";

import { useLazyGetDDLShipmentListQuery } from "store/services/shipmentApi";
import { useAuth } from "hooks/useAuth";
import { useLazyGetDDLWarehouseListQuery } from "store/services/warehouseApi";
import AppSwitch from "components/Shared/AppSwitch";
// import { CenterFocusWeakRounded } from "@mui/icons-material";

const INITIAL_STATE = {
  account: "",
  entity: "",
  allocation_id: "",
  warehouse_id: "",
  shipment_id: "",
  allocation_time: "",
  allocated_capacity: "",
  status: "",
  isActive: true,
};

const WarehouseAllocationForm = ({
  isBusy = false,
  allocationDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } =
  useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

    
  const [fetchWarehouses, { data: warehouses, isLoading: loadingWarehouses }] =
  useLazyGetDDLWarehouseListQuery();
  const [fetchShipments, { data: shipments, isLoading: loadingShipments }] =
  useLazyGetDDLShipmentListQuery();

  // React Hook Form setup
  const { control, handleSubmit, setValue, formState: { errors }, reset } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity:  viewEntity?.entity,
      account: viewEntity?.entity?.account || "",
            
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });

  // Handle form submission
  const handleOnSubmit = (data) => {
    const payload = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      warehouse_id: data?.warehouse_id?._id,
      shipmentId: data?.shipmentId?._id,
    };
    onSubmit(payload);
  };

  // Populate form for editing
  React.useEffect(() => {
    if (!allocationDetail) return;
    reset({
      ...INITIAL_STATE,
      ...allocationDetail,
      // allocation_time: moment(allocationDetail.allocation_time),
    });
  }, [allocationDetail, reset]);

  React.useEffect(() => {
          if (isAccountAdmin || isSystemAdmin)
              fetchEntities({ account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id });
      }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin, viewEntity?.entity?.account]);
  
      React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchWarehouses({account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [fetchWarehouses, _watchAccount?._id, _watchEntity?._id]);
  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchShipments({account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [fetchShipments, _watchAccount?._id, _watchEntity?._id]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={2}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Account"
                    required
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("entity", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Accounts..."
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} sm={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Entity"
                    required
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("warehouse_id", "");
                      // setValue("shipment_id", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {/* <Grid item xs={12} sm={6}>
            <Controller
              name="allocation_id"
              control={control}
              rules={{ required: "Please provide an Allocation ID." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Allocation ID"
                  error={!!errors.allocation_id}
                  helperText={errors?.allocation_id?.message}
                />
              )}
            />
          </Grid> */}

          <Grid item xs={12} sm={6}>
            <Controller
              name="warehouse_id"
              control={control}
              rules={{ required: "Please select a Warehouse." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Warehouse"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {onChange(data)}}
                  
                  getOptionLabel={(option) => option && `${option?.warehouse_id} - ${option?.name}`}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={warehouses || []}
                  loading={loadingWarehouses}
                  loadingText="Loading Warehouses..."
                  helperText={errors?.warehouse_id?.message}
                  error={!!errors?.warehouse_id}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select a Shipment." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Shipment"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={shipments || []}
                  loading={loadingShipments}
                  loadingText="Loading Shipments..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                />
              )}
            />
          </Grid>

<Grid item xs={12} sm={6}>
  <Controller
    name="allocation_time"
    control={control}
    rules={{ required: "Please provide an Allocation Time." }}
    render={({ field }) => (
      <TextField
        {...field}
        required
        fullWidth
        size="small"
        label="Allocation Time"
        type="datetime-local"
        InputLabelProps={{ shrink: true }}
        value={field.value ? new Date(field.value).toISOString().slice(0, 16) : ""} 
        onChange={field.onChange}
        error={!!errors.allocation_time}
        helperText={errors?.allocation_time?.message}
      />
    )}
  />
</Grid>



          <Grid item xs={12} sm={6}>
            <Controller
              name="allocated_capacity"
              control={control}
              rules={{
                required: "Please provide Allocated Capacity.",
                min: { value: 1, message: "Capacity must be at least 1." },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Allocated Capacity"
                  type="number"
                  error={!!errors.allocated_capacity}
                  helperText={errors?.allocated_capacity?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Please select a status." }}
              render={({ field }) => (
                <FormControl fullWidth size="small" error={!!errors.status}>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    {...field}
                    labelId="status-label"
                    id="status"
                    label="Status"
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Confirmed">Confirmed</MenuItem>
                    <MenuItem value="Fulfilled">Fulfilled</MenuItem>
                  </Select>
                  <FormHelperText>{errors?.status?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          {allocationDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch
                   {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      {/* Form Actions */}
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
        >
          {allocationDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default WarehouseAllocationForm;
